import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import DialogContent from '@mui/material/DialogContent';
import PropTypes from 'prop-types';
import {
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogHeader,
} from '@/components/common/CustomDialog';
import ConstituentSearchField from '@/components/common/form/ConstituentSearchField';
import ControlledDateInput from '@/components/common/form/ControlledDateInput';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import useAssigneeList from '@/lib/hooks/useAssigneeList';
import { useCurrentUser } from '@/lib/hooks/userData';
import translate from '@/lib/intl/translate';
import FileUploader from '@/modules/FileUploader';
import { TaskItemPropType } from '@/propTypes';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { useTaskServices } from '..';
import { getAssigneeOptions, getDefaultValues } from '../lib/taskFormUtils';
import { ModalType } from '../tasksConfig';
import AssigneeSelect from './AssigneeSelect';

const { CREATE, EDIT } = ModalType;

const CONSTITUENT_CLASSES = {
  textField: 'mb-0',
};

const TITLE_RULES = {
  [CREATE]: {
    required: translate('TASKS_VALIDATION_CREATE_TITLE_REQUIRED'),
  },
  [EDIT]: {
    required: translate('TASKS_VALIDATION_EDIT_TITLE_REQUIRED'),
  },
};

export default function CreateEditTaskDialog({
  constituentId,
  onClose,
  onSaveSuccess,
  task,
  type,
}) {
  const { createTask, updateTask } = useTaskServices();
  const defaultValues = React.useMemo(() => getDefaultValues(task), [task]);
  const formMethods = useForm({
    mode: 'all',
    defaultValues,
  });
  const { control, formState, handleSubmit, setValue } = formMethods;
  const { errors, isDirty, isSubmitting, isValid } = formState;
  const { assignees, isFetching: isFetchingAssignees } = useAssigneeList();
  const { userId: currentUserId } = useCurrentUser();
  const isCreate = type === CREATE;

  const assigneeOptions = React.useMemo(
    () =>
      getAssigneeOptions({
        assignees,
        currentUserId,
      }),
    [assignees, currentUserId],
  );

  const onSubmit = async (form) => {
    if (!isDirty || !isValid || isSubmitting) {
      return;
    }

    try {
      if (isCreate) {
        createTask(form, assignees);
      } else {
        updateTask(task, form, assignees);
      }
    } finally {
      if (onSaveSuccess) {
        onSaveSuccess();
      }

      onClose();
    }
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <DialogHeader
          headerText={translate(`TASKS_${isCreate ? 'CREATE' : 'EDIT'}_TASK`)}
          onClose={onClose}
        />

        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <DialogContent
            classes={{ root: 'grid grid-cols-1 gap-3 auto-rows-auto' }}
            dividers
          >
            <ControlledTextField
              control={control}
              error={!!errors?.taskTitle}
              helperText={errors?.taskTitle?.message}
              label={translate('TASKS_TASK_NAME')}
              name="taskTitle"
              required
              rules={TITLE_RULES[isCreate ? CREATE : EDIT]}
            />
            <AssigneeSelect
              disabled={isFetchingAssignees}
              options={assigneeOptions}
              name="developers"
            />
            <ControlledDateInput
              control={control}
              id="tasks-due-date"
              label={translate('TASKS_DATE')}
              name="dueDate"
            />
            <Controller
              name="constituents"
              render={({ field: { onChange, value } }) => (
                <ConstituentSearchField
                  classes={CONSTITUENT_CLASSES}
                  initialConstituentId={constituentId}
                  id="tasks-constituents"
                  label={translate('TASKS_CONSTITUENT')}
                  setValue={onChange}
                  value={value}
                />
              )}
            />
            <ControlledTextField
              className="mb-0"
              control={control}
              label={translate('TASKS_NOTES')}
              multiline
              rows={5}
              name="taskNotes"
            />
            <div>
              <div className="mb-4 type-h3">{translate('FILES')}</div>
              <FileUploader
                multiple
                onUploadFinished={(e) =>
                  setValue(
                    'attachments',
                    e.map(({ uploadId }) => uploadId),
                    { shouldDirty: true },
                  )
                }
                value={task?.attachments}
              />
            </div>
          </DialogContent>
          <DialogActions dividers>
            <DialogActionButton
              data-testid="task-create-edit-submit-btn"
              showLoader={isSubmitting}
              type="submit"
            >
              {translate(isCreate ? 'CREATE' : 'UPDATE')}
            </DialogActionButton>
            <DialogCancelButton onClick={onClose} />
          </DialogActions>
        </form>
      </FormProvider>
    </>
  );
}

CreateEditTaskDialog.propTypes = {
  constituentId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func,
  task: TaskItemPropType,
  type: PropTypes.oneOf([CREATE, EDIT]).isRequired,
};

CreateEditTaskDialog.defaultProps = {
  constituentId: '',
  onSaveSuccess: null,
  task: null,
};
